import { useEffect, useMemo, useState } from "react";
import React from "react";
import { http } from "@/api/client";
import { toast } from "sonner";
import { TrendingUp, BookOpen, Trophy, Clock, ChevronDown, ChevronRight } from "lucide-react";

const LEVELS = [
  { key: "quiz", label: "N1", color: "#1368ce" },
  { key: "difficile", label: "N2", color: "#d89e00" },
  { key: "legend", label: "Lég", color: "#e21b3c" },
];

export default function Progress() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ students: [], courses_total: 0 });
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await http.get("/admin/progress");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data.students;
    return data.students.filter(st => (st.name + " " + st.email).toLowerCase().includes(s));
  }, [data.students, search]);

  if (loading) return <div className="text-[#5b6577]" data-testid="progress-loading">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Progression des élèves</h1>
          <p className="mt-1 text-[#5b6577]">{data.students.length} élèves · {data.courses_total} cours au catalogue</p>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un élève…"
          className="bg-white border border-[#e5eaf3] rounded-xl px-4 py-2.5 text-sm w-64" data-testid="progress-search" />
      </div>

      <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8faff] text-[#5b6577] text-xs uppercase font-headings tracking-wide">
              <tr>
                <th className="text-left p-3 font-bold">Élève</th>
                <th className="text-left p-3 font-bold">Cours débloqués</th>
                <th className="text-left p-3 font-bold">Niveaux validés</th>
                <th className="text-left p-3 font-bold">Tentatives</th>
                <th className="text-left p-3 font-bold">Légende parfaits</th>
                <th className="text-left p-3 font-bold">Points</th>
                <th className="text-left p-3 font-bold">Dernière activité</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-[#5b6577]">Aucun élève.</td></tr>
              )}
              {filtered.map(st => {
                const isOpen = open === st.id;
                const pct = data.courses_total ? Math.round((st.courses_unlocked / data.courses_total) * 100) : 0;
                return (
                  <React.Fragment key={st.id}>
                    <tr className="border-t border-black/5 hover:bg-[#f8faff]/40" data-testid={`progress-row-${st.id}`}>
                      <td className="p-3">
                        <div className="font-headings font-semibold text-[#1a2332]">{st.name || "—"}</div>
                        <div className="text-xs text-[#9aa5bc]">{st.email}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5 text-[#00a86b]" />
                          <span className="font-semibold text-[#1a2332]">{st.courses_unlocked}/{st.courses_total}</span>
                          <div className="w-16 h-1.5 bg-[#e5eaf3] rounded-full overflow-hidden">
                            <div className="h-full bg-[#00a86b]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-semibold text-[#1a2332]">{st.levels_passed}</td>
                      <td className="p-3 text-[#5b6577]">{st.attempts_count}</td>
                      <td className="p-3">
                        {st.legend_perfect > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#e21b3c] font-bold">
                            <Trophy className="w-3.5 h-3.5" /> {st.legend_perfect}
                          </span>
                        ) : <span className="text-[#9aa5bc]">0</span>}
                      </td>
                      <td className="p-3 font-bold text-[#FF8C42]">{st.total_points.toLocaleString("fr-FR")}</td>
                      <td className="p-3 text-xs text-[#9aa5bc]">
                        {st.last_activity ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(st.last_activity).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="p-3">
                        <button onClick={() => setOpen(isOpen ? null : st.id)} data-testid={`expand-${st.id}`}
                          className="p-1.5 rounded hover:bg-[#00a86b]/10 text-[#00a86b]">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#f8faff]/60" data-testid={`detail-${st.id}`}>
                        <td colSpan={8} className="p-4">
                          {st.by_course.length === 0 ? (
                            <div className="text-sm text-[#5b6577]">Aucune tentative de quiz pour le moment.</div>
                          ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {st.by_course.sort((a,b) => (a.competency||0)-(b.competency||0) || a.course_id.localeCompare(b.course_id)).map(c => (
                                <div key={c.course_id} className="bg-white border border-black/5 rounded-xl p-3">
                                  <div className="text-xs font-mono text-[#9aa5bc]">C{c.competency} · {c.course_id}</div>
                                  <div className="font-headings font-semibold text-[#1a2332] text-sm mt-0.5 mb-2">{c.title}</div>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {LEVELS.map(l => {
                                      const r = c.levels[l.key];
                                      if (!r) return (
                                        <span key={l.key} className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[#f0f2f7] text-[#9aa5bc]">{l.label}</span>
                                      );
                                      return (
                                        <span key={l.key} title={`${r.correct}/${r.total} · ${r.points} pts`}
                                          className="text-[10px] font-bold rounded-full px-2 py-0.5"
                                          style={r.passed ? { backgroundColor: l.color, color: "white" } : { backgroundColor: l.color + "22", color: l.color }}>
                                          {l.label} {r.correct}/{r.total}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
